import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { SOAP_KEYS, pct, noteFullText, fmt12 } from "@/lib/soap";
import { copyText } from "@/lib/clipboard";

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900">{value || "—"}</div>
    </div>
  );
}

function DataTable({ head, rows }) {
  return (
    <table className="w-full text-sm border-collapse mt-2">
      <thead><tr>{head.map((h) => <th key={h} className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b py-1.5 pr-3">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="border-b border-slate-100 py-1.5 pr-3">{c}</td>)}</tr>)}</tbody>
    </table>
  );
}

export default function PrintNote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [n, setN] = useState(null);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/notes/${id}`).then((r) => setN(r.data)).catch(() => navigate("/")); }, [id, navigate]);
  if (!n) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const targets = n.targets.filter((t) => t.name);
  const behaviors = n.behaviors.filter((b) => b.name);
  const tpl = templates.find((t) => t.code === n.cpt_code);
  const tplRows = tpl ? tpl.fields.filter((f) => n.template_fields?.[f.key]).map((f) => [f.label, n.template_fields[f.key]]) : [];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="no-print max-w-[820px] mx-auto mb-4 flex justify-between">
        <Button variant="ghost" onClick={() => navigate(`/notes/${id}`)} data-testid="print-back-button"><ArrowLeft className="h-4 w-4 mr-2" />Back to note</Button>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" disabled={!SOAP_KEYS.some((s) => n[s.key]?.trim())} onClick={() => copyText(noteFullText(n, tpl), "Full note copied — paste it into your medical platform")} data-testid="print-copy-button"><Copy className="h-4 w-4 mr-2" />Copy note</Button>
          <Button className="rounded-full" onClick={() => window.print()} data-testid="print-pdf-button"><Printer className="h-4 w-4 mr-2" />Print / Save as PDF</Button>
        </div>
      </div>
      <article className="print-sheet max-w-[820px] mx-auto bg-white shadow-sm border rounded-lg p-10 sm:p-12" data-testid="print-sheet">
        <header className="flex justify-between items-start border-b-2 border-primary pb-4">
          <div>
            <div className="font-display text-xl font-bold text-primary">Beyond Boundaries ABA Scribe</div>
            <div className="text-xs text-slate-500 uppercase tracking-[0.18em]">ABA Session Note · SOAP</div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>Status: <span className="font-semibold text-slate-800">{n.status === "final" ? "Final" : "Draft"}</span></div>
            <div>Updated {new Date(n.updated_at).toLocaleDateString()}</div>
          </div>
        </header>
        <div className="grid grid-cols-3 gap-4 py-5 border-b">
          <Meta label="Client" value={n.client_name} />
          <Meta label="Date" value={n.session_date} />
          <Meta label="Time" value={n.start_time && `${fmt12(n.start_time)} – ${fmt12(n.end_time)}`} />
          <Meta label="Setting" value={n.setting} />
          <Meta label="CPT code" value={n.cpt_code} />
          <Meta label="Provider" value={n.provider_name && `${n.provider_name}, ${n.provider_role}`} />
        </div>
        {tplRows.length > 0 && (
          <section className="py-4" data-testid="print-template-section">
            <h4 className="text-sm font-semibold">{tpl.name}</h4>
            <DataTable head={["Item", "Details"]} rows={tplRows} />
          </section>
        )}
        {targets.length > 0 && (
          <section className="py-4">
            <h4 className="text-sm font-semibold">Skill acquisition data</h4>
            <DataTable head={["Target", "Correct / Trials", "%", "Prompt"]} rows={targets.map((t) => [t.name, `${t.correct}/${t.total}`, `${pct(t.correct, t.total)}%`, t.prompt_level || "—"])} />
          </section>
        )}
        {behaviors.length > 0 && (
          <section className="py-4">
            <h4 className="text-sm font-semibold">Behavior data</h4>
            <DataTable head={["Behavior", "Frequency", "Duration", "Notes"]} rows={behaviors.map((b) => [b.name, b.frequency, b.duration || "—", b.notes || "—"])} />
          </section>
        )}
        {SOAP_KEYS.map((s) => (
          <section key={s.key} className="py-4 break-inside-avoid">
            <h4 className="font-display font-semibold text-primary">{s.letter} — {s.label}</h4>
            <p className="mt-1.5 text-[14px] leading-relaxed whitespace-pre-wrap text-slate-800">{n[s.key] || "—"}</p>
          </section>
        ))}
        <footer className="grid grid-cols-2 gap-10 pt-12">
          {["Provider signature / date", "Supervisor (BCBA) signature / date"].map((l) => (
            <div key={l}><div className="border-b border-slate-400 h-8" /><div className="text-[11px] text-slate-500 mt-1">{l}</div></div>
          ))}
        </footer>
      </article>
    </div>
  );
}
